import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, Download, TrendingUp, Wallet, AlertTriangle, ArrowRight,
  ChevronLeft
} from "lucide-react";
import {
  fetchEmployeeUsage, fetchTenantUsage
} from "../../services/api";
import { computeOverageCents, formatZAR, PLANS } from "../../services/plans";
import { usePacks, tenantMonthlyCents } from "../../services/packs";
import { COST_RATES, INTEGRATION_KEYS } from "../../services/costRates";
import type {
  CostBreakdown, IntegrationKey, Tenant, TenantUser, UsageRange
} from "../../types";
import { StackedAreaChart } from "../../components/charts/StackedAreaChart";
import { DonutChart } from "../../components/charts/DonutChart";
import { HorizontalBars } from "../../components/charts/HorizontalBars";

interface Props { tenant: Tenant; users: TenantUser[] }

const RANGES: { label: string; value: UsageRange }[] = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "This month", value: "this_month" },
  { label: "Last month", value: "last_month" },
  { label: "Year to date", value: "ytd" }
];

export function UsageTab({ tenant, users }: Props) {
  const { bySlug } = usePacks();
  const [range, setRange] = useState<UsageRange>("30d");
  const [breakdown, setBreakdown] = useState<CostBreakdown | null>(null);
  const [drill, setDrill] = useState<{ userId: string | null; userName: string } | null>(null);
  const [drillBreakdown, setDrillBreakdown] = useState<CostBreakdown | null>(null);

  useEffect(() => {
    fetchTenantUsage(tenant.id, range).then((r) => setBreakdown(r.breakdown));
  }, [tenant.id, range]);

  useEffect(() => {
    if (!drill || drill.userId == null) { setDrillBreakdown(null); return; }
    fetchEmployeeUsage(tenant.id, drill.userId, range).then((r) => setDrillBreakdown(r.breakdown));
  }, [drill, tenant.id, range]);

  if (!breakdown) return <div className="muted">Loading usage…</div>;

  const pack = tenant.pack ? bySlug[tenant.pack] : undefined;
  const planRevenueCents = tenantMonthlyCents(tenant, pack?.perRepMonthlyCents ?? null, pack?.flatMonthlyCents ?? null);
  // Normalize cost to monthly equivalent for margin signal
  const days = Math.max(1, (new Date(breakdown.rangeEnd).getTime() - new Date(breakdown.rangeStart).getTime()) / 86400000);
  const monthlyCost = (breakdown.totalCents / days) * 30;
  const margin = planRevenueCents - monthlyCost;
  const marginPct = planRevenueCents > 0 ? (margin / planRevenueCents) * 100 : 0;

  function downloadCsv() {
    const lines = ["day,integration,units,cost_zar_cents"];
    for (const day of breakdown!.byDay) {
      for (const k of INTEGRATION_KEYS) {
        const c = day.byIntegration[k];
        if (c > 0) lines.push(`${day.day},${k},,${c}`);
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tenant.slug}-usage-${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="col" style={{ gap: 18 }}>
      {/* Filter bar */}
      <div className="card" style={{ padding: 14 }}>
        <div className="row wrap between" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Calendar size={14} style={{ color: "var(--text-mute)" }} />
            {RANGES.map((r) => (
              <button
                key={r.value}
                className={`tab ${range === r.value ? "active" : ""}`}
                onClick={() => { setRange(r.value); setDrill(null); }}
                style={{ background: "transparent", border: "1px solid var(--border)" }}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>
              {new Date(breakdown.rangeStart).toLocaleDateString("en-ZA")} → {new Date(breakdown.rangeEnd).toLocaleDateString("en-ZA")}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={downloadCsv}>
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid-4">
        <div className="card kpi">
          <h3>Cost in window</h3>
          <div className="value tnum">{formatZAR(breakdown.totalCents)}</div>
          <div className="delta">{Math.round(days)} days · {formatZAR(Math.round(breakdown.totalCents / days))} / day avg</div>
        </div>
        <div className="card kpi">
          <h3>Projected monthly cost</h3>
          <div className="value tnum">{formatZAR(Math.round(monthlyCost))}</div>
          <div className="delta">based on this window's daily rate</div>
        </div>
        <div className="card kpi">
          <h3>Package revenue</h3>
          <div className="value tnum">{formatZAR(planRevenueCents)}</div>
          <div className="delta">monthly · {pack?.label ?? "no package"}</div>
        </div>
        <div className="card kpi">
          <h3>Estimated margin</h3>
          <div className="value tnum" style={{ color: margin >= 0 ? "var(--success)" : "var(--danger)" }}>
            {formatZAR(Math.round(margin))}
          </div>
          <div className={`delta ${margin >= 0 ? "up" : "down"}`}>
            {margin >= 0 ? <TrendingUp size={12} /> : <AlertTriangle size={12} />}
            {marginPct.toFixed(0)}% margin
          </div>
        </div>
      </div>

      {/* Trend + breakdown */}
      <div className="grid-2">
        <div className="card" style={{ gridColumn: "span 1" }}>
          <div className="section-head">
            <h2><TrendingUp size={16} /> Cost trend</h2>
            <span className="muted">Stacked by integration</span>
          </div>
          <StackedAreaChart data={breakdown.byDay} series={INTEGRATION_KEYS} />
        </div>
        <div className="card">
          <div className="section-head">
            <h2><Wallet size={16} /> Cost by integration</h2>
          </div>
          <DonutChart data={breakdown.byIntegration} />
        </div>
      </div>

      {/* Plan allowance utilization — surfaces which API is closest to overage
          for this tenant, and projects month-end overage charges. */}
      <PlanUtilization tenant={tenant} />

      {/* Integration detail table */}
      <div className="card">
        <div className="section-head"><h2>Integration detail</h2></div>
        <div className="table-wrap" style={{ border: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Integration</th>
                <th>Unit</th>
                <th>Units consumed</th>
                <th>Unit cost (ZAR)</th>
                <th>Total cost</th>
                <th>% of spend</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.byIntegration.map((row) => {
                const rate = COST_RATES[row.integration];
                const pct = breakdown.totalCents === 0 ? 0 : (row.cents / breakdown.totalCents) * 100;
                return (
                  <tr key={row.integration}>
                    <td>
                      <span className="row" style={{ gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: rate.color }} />
                        {rate.label}
                      </span>
                    </td>
                    <td className="muted">{rate.unit}</td>
                    <td className="tnum">{Intl.NumberFormat("en-ZA", { notation: "compact" }).format(row.units)}</td>
                    <td className="tnum muted mono">{(rate.zarCentsPerUnit / 100).toFixed(6)}</td>
                    <td className="tnum">{formatZAR(row.cents)}</td>
                    <td className="tnum muted">{pct.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee drill or leaderboard */}
      {drill ? (
        <div className="card">
          <div className="section-head">
            <h2 className="row" style={{ gap: 8 }}>
              <button className="icon-btn" onClick={() => setDrill(null)}><ChevronLeft size={14} /></button>
              {drill.userName} · usage
            </h2>
            <span className="muted">in {RANGES.find((r) => r.value === range)?.label.toLowerCase()}</span>
          </div>
          {!drillBreakdown ? (
            <div className="muted">Loading employee usage…</div>
          ) : (
            <div className="grid-2">
              <DonutChart data={drillBreakdown.byIntegration} centerLabel="By integration" />
              <StackedAreaChart data={drillBreakdown.byDay} series={INTEGRATION_KEYS} height={180} />
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="section-head">
            <h2>Cost by employee</h2>
            <span className="muted">Top spenders first · click to drill in</span>
          </div>
          <HorizontalBars
            rows={breakdown.byEmployee.map((e) => ({
              label: e.userName,
              value: e.cents,
              sub: `${e.events.toLocaleString()} events`
            }))}
          />
          <div className="col" style={{ gap: 6, marginTop: 14 }}>
            {breakdown.byEmployee.map((e) => (
              <button
                key={e.userId ?? "_sys"}
                onClick={() => e.userId && setDrill({ userId: e.userId, userName: e.userName })}
                className="row between"
                disabled={!e.userId}
                style={{
                  padding: "10px 14px",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  width: "100%",
                  cursor: e.userId ? "pointer" : "default",
                  color: "var(--text)",
                  fontSize: 13
                }}
              >
                <span className="row" style={{ gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{e.userName}</span>
                  <span className="muted" style={{ fontSize: 11.5 }}>{e.events.toLocaleString()} events</span>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <span className="tnum">{formatZAR(e.cents)}</span>
                  {e.userId && <ArrowRight size={14} style={{ opacity: 0.5 }} />}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Plan allowance utilization card.
 *
 * For each metered metric (voice minutes, WhatsApp, AI tokens) shows:
 *   - current month consumption
 *   - plan-included allowance
 *   - % consumed
 *   - day-of-month progress vs % consumed (red if consuming faster than time)
 *   - projected month-end usage at current burn rate
 *   - projected overage charges if usage exceeds the cap
 *
 * This directly answers "which API is costing this client the most?" — the
 * row with the highest projected overage is the cost lever.
 */
function PlanUtilization({ tenant }: { tenant: Tenant }) {
  const plan = PLANS[tenant.plan];
  // The tenant's real ceilings: per-rep allowances scaled by team size, plus
  // any operator override. The plan defaults are only a fallback.
  const limits = tenant.effectiveLimits ?? plan.defaultLimits;
  const usage = tenant.usage;

  // How far through the month are we (0..1)? Used to flag tenants who'll
  // blow their cap if current burn continues.
  const now = new Date();
  const daysInMonth = new Date(now.getUTCFullYear(), now.getUTCMonth() + 1, 0).getUTCDate();
  const dayOfMonth = now.getUTCDate();
  const monthProgress = dayOfMonth / daysInMonth;
  const burnFactor = monthProgress > 0 ? 1 / monthProgress : 0;

  const rows = [
    {
      label: "Voice minutes",
      used: usage.voiceMinutesThisMonth,
      cap: limits.voiceMinutesPerMonth,
      color: COST_RATES.twilio_voice.color,
      unit: "min"
    },
    {
      label: "WhatsApp messages",
      used: usage.whatsappMessagesThisMonth,
      cap: limits.whatsappMessagesPerMonth,
      color: COST_RATES.twilio_whatsapp.color,
      unit: "msg"
    },
    {
      label: "AI tokens",
      used: usage.aiTokensThisMonth,
      cap: limits.aiTokensPerMonth,
      color: COST_RATES.openai.color,
      unit: "tok"
    }
  ];

  // Project end-of-month usage at the current daily burn rate.
  const projected = {
    voiceMinutes: Math.round(usage.voiceMinutesThisMonth * burnFactor),
    whatsappMessages: Math.round(usage.whatsappMessagesThisMonth * burnFactor),
    aiTokens: Math.round(usage.aiTokensThisMonth * burnFactor)
  };
  const projectedOverage = computeOverageCents(tenant.plan, projected, limits);

  // Identify the costliest API for this tenant — the row contributing the
  // most to projected overage (or the highest absolute utilization if no
  // overage projected).
  const costliest = rows.reduce((max, r) => {
    const pct = r.cap > 0 ? r.used / r.cap : 0;
    return pct > max.pct ? { row: r, pct } : max;
  }, { row: rows[0], pct: 0 });

  return (
    <div className="card">
      <div className="section-head">
        <h2><Wallet size={16} /> Plan allowance — this month</h2>
        <span className="muted">
          Day {dayOfMonth} of {daysInMonth} · ideal pace ≤ {Math.round(monthProgress * 100)}% used
        </span>
      </div>

      <div className="col" style={{ gap: 14 }}>
        {rows.map((r) => {
          const pct = r.cap > 0 ? Math.min(100, (r.used / r.cap) * 100) : 0;
          const onTrack = r.cap > 0 ? r.used / r.cap <= monthProgress * 1.1 : true;
          const projectedUnits = Math.round(r.used * burnFactor);
          const projectedOver = Math.max(0, projectedUnits - r.cap);
          return (
            <div key={r.label}>
              <div className="row between" style={{ marginBottom: 6 }}>
                <span className="row" style={{ gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color }} />
                  <strong>{r.label}</strong>
                  {!onTrack && (
                    <span className="pill warning" style={{ fontSize: 11 }}>burning fast</span>
                  )}
                </span>
                <span className="muted tnum" style={{ fontSize: 12 }}>
                  {Intl.NumberFormat("en-ZA").format(r.used)} / {Intl.NumberFormat("en-ZA").format(r.cap)} {r.unit}
                  {" · "}
                  <strong style={{ color: pct > 90 ? "var(--danger)" : pct > 75 ? "var(--warning)" : "var(--text)" }}>
                    {pct.toFixed(0)}%
                  </strong>
                </span>
              </div>
              <div style={{ position: "relative", height: 8, background: "var(--surface)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  position: "absolute",
                  inset: 0,
                  width: `${pct}%`,
                  background: pct > 90 ? "var(--danger)" : pct > 75 ? "var(--warning)" : r.color,
                  borderRadius: 4
                }} />
                <div style={{
                  position: "absolute",
                  left: `${monthProgress * 100}%`,
                  top: -2,
                  bottom: -2,
                  width: 2,
                  background: "var(--text-mute)",
                  opacity: 0.6
                }} title="Today's calendar position" />
              </div>
              {projectedOver > 0 && (
                <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                  Projected month-end: {Intl.NumberFormat("en-ZA").format(projectedUnits)} {r.unit}
                  {" → "}<strong style={{ color: "var(--danger)" }}>
                    {Intl.NumberFormat("en-ZA").format(projectedOver)} {r.unit} over
                  </strong>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary tile: projected overage + costliest API */}
      <div className="row between" style={{ marginTop: 18, padding: 14, background: "var(--surface)", borderRadius: "var(--radius-sm)" }}>
        <div>
          <div className="muted" style={{ fontSize: 11.5 }}>Projected month-end overage</div>
          <div className="tnum" style={{ fontSize: 20, fontWeight: 700, color: projectedOverage.totalCents > 0 ? "var(--warning)" : "var(--success)" }}>
            {formatZAR(projectedOverage.totalCents)}
          </div>
          {projectedOverage.totalCents > 0 && (
            <div className="muted" style={{ fontSize: 11 }}>
              voice {formatZAR(projectedOverage.voiceCents)}
              · WhatsApp {formatZAR(projectedOverage.whatsappCents)}
              · AI {formatZAR(projectedOverage.aiCents)}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="muted" style={{ fontSize: 11.5 }}>Costliest API right now</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{costliest.row.label}</div>
          <div className="muted" style={{ fontSize: 11 }}>{(costliest.pct * 100).toFixed(0)}% of plan</div>
        </div>
      </div>
    </div>
  );
}
